
import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { format } from 'date-fns';
import { Loader2, Info, Search, BarChart2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FilterBar } from './FilterBar';
import { PatrolActivity, PatrolFilters } from './types';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShieldAlert } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface PatrolLogsViewProps {
  teamId: string;
}

const PatrolLogsContent = ({ teamId }: PatrolLogsViewProps) => {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined,
    to: undefined,
  });

  const [filters, setFilters] = useState<PatrolFilters>({
    types: [],
    status: [],
    guardName: '',
  });

  const { data: activities, isLoading } = useQuery({
    queryKey: ['patrol-activities', teamId, dateRange],
    queryFn: async () => {
      const allActivities: PatrolActivity[] = [];

      try {
        console.log('Fetching patrol activities for team ID:', teamId);
        
        // 2. Get sites that belong to this team
        const { data: teamSites, error: sitesError } = await supabase
          .from('guardian_sites')
          .select('id')
          .eq('team_id', teamId);
          
        if (sitesError) {
          console.error('Error fetching team sites:', sitesError);
          throw sitesError;
        }
        
        if (!teamSites || teamSites.length === 0) {
          console.log(`No sites found for team ${teamId}`);
          return [];
        }
        
        const siteIds = teamSites.map(site => site.id);
        console.log(`Found ${siteIds.length} sites for team ${teamId}`);
        
        // 3. Fetch patrol sessions for team sites only (regardless of which guard)
        let patrolSessionsQuery = supabase
          .from('patrol_sessions' as any)
          .select(`
            id,
            guard_id,
            site_id,
            start_time,
            end_time,
            status,
            profiles:guard_id(full_name),
            guardian_sites:site_id(name)
          `)
          .in('site_id', siteIds)
          .order('start_time', { ascending: false });

        if (dateRange.from) {
          patrolSessionsQuery = patrolSessionsQuery.gte('start_time', dateRange.from.toISOString());
        }
        if (dateRange.to) {
          patrolSessionsQuery = patrolSessionsQuery.lte('start_time', dateRange.to.toISOString());
        }

        const { data: sessions, error: sessionsError } = await patrolSessionsQuery;

        if (sessionsError) {
          console.error('Error fetching patrol sessions:', sessionsError);
          throw sessionsError;
        } 
        
        if (!sessions || sessions.length === 0) {
          console.log(`No patrol sessions found for team ${teamId} sites`);
          return [];
        }
        
        console.log(`Found ${sessions.length} patrol sessions for team ${teamId} sites`);
        
        // Extract session IDs for further queries
        const sessionIds = (sessions as any[]).map(session => session.id);
        
        // Add patrol start/end activities
        for (const session of (sessions as any[])) {
          allActivities.push({
            id: `${session.id}-start`,
            timestamp: session.start_time,
            type: 'patrol_start',
            guard_name: session.profiles?.full_name || 'Unknown',
              location: session.guardian_sites?.name || 'Unknown Site',
            status: session.status || 'active',
            description: 'Patrol started'
          });
          
          if (session.end_time) {
            allActivities.push({
              id: `${session.id}-end`,
              timestamp: session.end_time,
              type: 'patrol_end',
              guard_name: session.profiles?.full_name || 'Unknown',
                location: session.guardian_sites?.name || 'Unknown Site',
              status: 'completed',
              description: 'Patrol ended'
            });
          }
        }

        // 4. Fetch checkpoint visits using the session IDs from team sites
        if (sessionIds.length > 0) {
          const { data: checkpoints, error: checkpointsError } = await supabase
            .from('patrol_checkpoint_visits' as any)
            .select(`
              id,
              patrol_id,
              checkpoint_id,
              status,
              timestamp,
              notes,
              latitude,
              longitude,
              patrol_sessions(guard_id, sites:site_id(name)),
              guardian_checkpoints:checkpoint_id(name)
            `)
            .in('patrol_id', sessionIds);

          if (checkpointsError) {
            console.error('Error fetching checkpoints:', checkpointsError);
          } else if (checkpoints && checkpoints.length > 0) {
            console.log(`Found ${checkpoints.length} checkpoint visits for team ${teamId} sites`);
            
            // Get guard names in a single query
            const guardIds = [...new Set((checkpoints as any[])
              .map(c => c.patrol_sessions?.guard_id)
              .filter(Boolean))];
              
            const { data: guards } = await supabase
              .from('profiles')
              .select('id, full_name')
              .in('id', guardIds);

            const guardMap = Object.fromEntries(
              (guards || []).map(g => [g.id, g.full_name])
            );

            for (const checkpoint of (checkpoints as any[])) {
              const guardId = checkpoint.patrol_sessions?.guard_id;
              allActivities.push({
                id: checkpoint.id,
                timestamp: checkpoint.timestamp,
                type: 'checkpoint',
                guard_name: guardId ? guardMap[guardId] || 'Unknown' : 'Unknown',
                location: checkpoint.guardian_checkpoints?.name || 'Unknown Checkpoint',
                status: checkpoint.status,
                description: checkpoint.notes || `Checkpoint scanned: ${checkpoint.guardian_checkpoints?.name || 'Unknown'}`
              });
            }
          } else {
            console.log(`No checkpoint visits found for team ${teamId} sites`);
          }

          // 5. Fetch incidents using the session IDs from team sites
          const { data: incidents, error: incidentsError } = await supabase
            .from('patrol_incidents' as any)
            .select(`
              id,
              patrol_id,
              title,
              description,
              severity,
              status,
              timestamp,
              patrol_sessions(guard_id)
            `)
            .in('patrol_id', sessionIds);

          if (incidentsError) {
            console.error('Error fetching incidents:', incidentsError);
          } else if (incidents && incidents.length > 0) {
            console.log(`Found ${incidents.length} incidents for team ${teamId} sites`);
            
            const guardIds = [...new Set((incidents as any[])
              .map(i => i.patrol_sessions?.guard_id)
              .filter(Boolean))];
              
            const { data: guards } = await supabase
              .from('profiles')
              .select('id, full_name')
              .in('id', guardIds);

            const guardMap = Object.fromEntries(
              (guards || []).map(g => [g.id, g.full_name])
            );

            for (const incident of (incidents as any[])) {
              const guardId = incident.patrol_sessions?.guard_id;
              allActivities.push({
                id: incident.id,
                timestamp: incident.timestamp,
                type: 'incident',
                guard_name: guardId ? guardMap[guardId] || 'Unknown' : 'Unknown',
                location: 'Patrol Location',
                status: incident.status,
                description: `${incident.title}: ${incident.description}`,
                severity: incident.severity
              });
            }
          } else {
            console.log(`No incidents found for team ${teamId} sites`);
          }

          // 6. Fetch alerts using the session IDs from team sites
          const { data: alerts, error: alertsError } = await supabase
            .from('patrol_alerts' as any)
            .select(`
              id,
              patrol_id,
              alert_type,
              description,
              resolved,
              created_at,
              patrol_sessions(guard_id)
            `)
            .in('patrol_id', sessionIds);

          if (alertsError) {
            console.error('Error fetching alerts:', alertsError);
          } else if (alerts && alerts.length > 0) {
            console.log(`Found ${alerts.length} alerts for team ${teamId} sites`);
            
            const guardIds = [...new Set((alerts as any[])
              .map(a => a.patrol_sessions?.guard_id)
              .filter(Boolean))];
              
            const { data: guards } = await supabase
              .from('profiles')
              .select('id, full_name')
              .in('id', guardIds);

            const guardMap = Object.fromEntries(
              (guards || []).map(g => [g.id, g.full_name])
            );

            for (const alert of (alerts as any[])) {
              const guardId = alert.patrol_sessions?.guard_id;
              allActivities.push({
                id: alert.id,
                timestamp: alert.created_at,
                type: 'alert',
                guard_name: guardId ? guardMap[guardId] || 'Unknown' : 'Unknown',
                location: 'Patrol Location',
                status: alert.resolved ? 'resolved' : 'pending',
                description: alert.description,
                severity: alert.alert_type
              });
            }
          } else {
            console.log(`No alerts found for team ${teamId} sites`);
          }
        }

        // Sort activities by timestamp (newest first)
        return allActivities.sort((a, b) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

      } catch (error) {
        console.error('Error in patrol activities query:', error);
        return [];
      }
    }
  });

  const filteredActivities = activities?.filter(activity => {
    const matchesType = filters.types.length === 0 || filters.types.includes(activity.type);
    const matchesStatus = filters.status.length === 0 || filters.status.includes(activity.status);
    const matchesGuard = !filters.guardName || 
      activity.guard_name.toLowerCase().includes(filters.guardName.toLowerCase());
    
    return matchesType && matchesStatus && matchesGuard;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      active: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
      completed: 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/40',
      pending: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700',
      resolved: 'bg-muted text-muted-foreground border-border'
    };

    return (
      <Badge 
        variant="outline" 
        className={`${statusStyles[status] || 'bg-muted text-muted-foreground border-border'} font-medium`}
      >
        {status}
      </Badge>
    );
  };

  const getTypeIcon = (type: string) => {
    const typeStyles: Record<string, string> = {
      patrol_start: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700',
      patrol_end: 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/40',
      checkpoint: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
      incident: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
      alert: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700'
    };

    return (
      <Badge 
        variant="outline" 
        className={`${typeStyles[type] || 'bg-gray-50 text-gray-700'} font-medium`}
      >
        {type.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <div className="h-full flex flex-col animate-fadeIn">
      {/* Fixed header section - not scrollable */}
      <div className="space-y-4 p-2 sm:p-4 flex-shrink-0">
        {/* Header Section with Glass Effect */}
        <div className="bg-gradient-to-r from-primary-50/80 to-primary-100/50 backdrop-blur-sm p-3 sm:p-4 rounded-lg border border-primary-100 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="w-full sm:w-auto">
              <DateRangePicker
                onUpdate={setDateRange}
                initialDateFrom={dateRange.from}
                initialDateTo={dateRange.to}
                className="w-full sm:w-auto"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <BarChart2 className="h-4 w-4" />
              <span>Patrol Activity Logs</span>
            </div>
          </div>
        </div>

        {/* Filters Section - Made scrollable on mobile */}
        <div className="overflow-x-auto pb-2">
          <div className="min-w-max">
            <FilterBar filters={filters} onFiltersChange={setFilters} />
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-grow overflow-hidden px-2 sm:px-4">
        <Card className="h-full overflow-hidden border-primary-100/20 bg-card/50 backdrop-blur-sm shadow-xl">
          <ScrollArea className="h-full">
            <div className="p-1">
              <Table>
                <TableHeader className="bg-primary-50/50 backdrop-blur-sm sticky top-0 z-10">
                  <TableRow>
                    <TableHead className="text-primary-700 font-medium whitespace-nowrap min-w-[120px]">Time</TableHead>
                    <TableHead className="text-primary-700 font-medium whitespace-nowrap min-w-[100px]">Type</TableHead>
                    <TableHead className="text-primary-700 font-medium whitespace-nowrap min-w-[120px]">Guard</TableHead>
                    <TableHead className="text-primary-700 font-medium whitespace-nowrap min-w-[120px]">Location</TableHead>
                    <TableHead className="text-primary-700 font-medium whitespace-nowrap min-w-[100px]">Status</TableHead>
                    <TableHead className="text-primary-700 font-medium whitespace-nowrap min-w-[200px]">Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredActivities?.map((activity) => (
                    <TableRow 
                      key={activity.id}
                      className="hover:bg-primary-50/30 transition-colors"
                    >
                      <TableCell className="font-mono text-sm whitespace-nowrap">
                        {format(new Date(activity.timestamp), 'PPp')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{getTypeIcon(activity.type)}</TableCell>
                      <TableCell className="whitespace-nowrap">{activity.guard_name || 'Unknown'}</TableCell>
                      <TableCell className="whitespace-nowrap">{activity.location || 'N/A'}</TableCell>
                      <TableCell className="whitespace-nowrap">{getStatusBadge(activity.status)}</TableCell>
                      <TableCell className="max-w-[200px] sm:max-w-md truncate">
                        {activity.description || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!filteredActivities?.length && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-center px-4">
                          <Info className="h-10 w-10 sm:h-12 sm:w-12 text-primary-300 mb-4" />
                          <h3 className="text-base sm:text-lg font-medium text-primary-700 mb-2">
                            No patrol activities found
                          </h3>
                          <p className="text-sm text-muted-foreground max-w-sm">
                            No patrol logs were found for this team's sites. Activities will appear here when guards patrol at sites belonging to this team.
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
};

export const PatrolLogsView = ({ teamId }: PatrolLogsViewProps) => {
  const { isAdmin, hasRole } = useUserRoles();
  const isClient = hasRole('client');

  if (!isAdmin && !isClient) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You do not have permission to view patrol logs. This feature is only available to administrators and clients.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return <PatrolLogsContent teamId={teamId} />;
};

export default PatrolLogsView;
